import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Select } from "src/base-components/Select";
import {
  CustomConnectionForm,
  JinjaBodyContentType,
} from "src/customConnectionNode/types";

type Props = { immutable: boolean; runFieldErrors: FieldErrors | undefined };

const contentTypeOptions: {
  key: JinjaBodyContentType;
  value: string;
  description: string;
}[] = [
  {
    key: "json",
    value: "Resolve to JSON",
    description: "The template will resolve to a JSON object",
  },
  {
    key: "xml",
    value: "Resolve to XML",
    description: "The template will resolve to an XML object",
  },
  {
    key: "raw",
    value: "Raw",
    description: "The resolved template will be sent as is",
  },
];

export const JinjaBodyEditor: React.FC<Props> = ({
  immutable,
  runFieldErrors,
}) => {
  const { getValues, setValue, control } =
    useFormContext<CustomConnectionForm>();

  const bodyId = getValues("body.id");
  const templateValue = getValues("body.value.template");
  const sourceId = getValues("body.value.source.id");

  const templateError = runFieldErrors && runFieldErrors[bodyId];
  const sourceError = runFieldErrors && runFieldErrors[sourceId];

  return (
    <div className="flex flex-col gap-[10px]">
      <div className="flex flex-col gap-0.5">
        <span className="mb-0.5 block font-inter-semibold-13px text-gray-800">
          Data source
        </span>
        <span className="mb-1 block font-inter-normal-12px text-gray-500">
          Pass in a dictionary to use in your template and reference it in your
          template as{" "}
          <code className="rounded bg-gray-100 px-1 font-fira-code">query</code>
        </span>
        <Controller
          control={control}
          name="body.value.source.expression"
          render={({ field }) => (
            <AutocompleteCodeInput
              dataLoc="jinja-body-source"
              placeholder='e.g. data, data.some_obj, {"key": "value"}'
              disabled={immutable}
              error={sourceError}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="h-[560px]">
        <CodeEditor
          language={undefined}
          dataLoc="jinja-body-editor"
          disabled={immutable}
          value={templateValue}
          onChange={(content) => {
            if (content !== undefined) {
              setValue("body.value.template", content);
            }
          }}
        />
      </div>
      {templateError && (
        <p className="my-2 font-inter-normal-12px text-red-500">
          {templateError}
        </p>
      )}
      <div>
        <span className="mb-1 block font-inter-semibold-13px text-gray-800">
          Request body format
        </span>
        <Controller
          control={control}
          name="body.value.content_type"
          render={({ field }) => (
            <Select
              dataLoc="jinja-body-content-type"
              disabled={immutable}
              fullWidth
              options={contentTypeOptions}
              placeholder="Select request body format"
              value={field.value}
              onChange={(value) => {
                field.onChange(value as JinjaBodyContentType);
              }}
            />
          )}
        />
      </div>
    </div>
  );
};
